/**
 * Close out payment (DNKADM-0408 U2) — the small dialog an owner or admin uses to end a
 * stuck card payment that Check payment could not settle on its own (DNKAPI-0565).
 *
 * **It never charges or refunds.** Staff record what they decided, with a reason:
 *  - "This payment belongs to the booking" (`settle_to_payment`) names the charge, by its
 *    payment id, that is this booking's payment → the booking reads Paid;
 *  - "Nothing was charged, or it was already refunded" (`no_charge`) → the booking reads
 *    Unpaid.
 * No amount is computed or sent here.
 *
 * The confirm button names the action ("Mark paid" / "Mark unpaid"). A `409
 * RESERVATION_CLOSE_OUT_PAYMENT_INVALID` is shown next to the payment id field, in the
 * server's words. The two answers that end the dialog — the payment was already resolved
 * (`409 RESERVATION_PAYMENT_NOT_STRANDED`), and a `403` — are raised to the host, which
 * re-reads the booking or withdraws the control.
 */
import { useId, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconAction } from '@/components/IconAction';
import { Icon, resolveTokens } from '@/theme';
import {
  closeOutReservationPayment,
  type PaymentCloseOutResolution,
  type Reservation,
  type ReservationPaymentCloseOut,
} from '@/api/reservations';
import {
  isCloseOutForbidden,
  isCloseOutPaymentInvalid,
  isPaymentNotStrandedRefusal,
  toCloseOutPaymentErrorMessage,
} from './reservationsError';

/** dink-api's reason limit (`CLOSE_OUT_REASON_MAX`), measured after trimming. */
export const CLOSE_OUT_REASON_MAX = 500;

export const CLOSE_OUT_PAYMENT_ID_HELP =
  'Copy it from the card processor or the needs-review notification.';

interface CloseOutPaymentDialogProps {
  open: boolean;
  /** The stuck booking being closed out. */
  reservation: Reservation;
  onClose: () => void;
  /**
   * The close-out landed: the server's answer, for the host to reflect, and the resolution
   * this dialog SENT — the fallback when the answer's own `resolution` is one the console
   * does not know (parsed to null).
   */
  onClosedOut: (result: ReservationPaymentCloseOut, sent: PaymentCloseOutResolution) => void;
  /** `409 RESERVATION_PAYMENT_NOT_STRANDED`: someone (or the feed) resolved it first. */
  onAlreadyResolved: () => void;
  /** `403`: this session may not close out; the host stops offering it. */
  onForbidden: () => void;
}

export function CloseOutPaymentDialog({
  open,
  reservation,
  onClose,
  onClosedOut,
  onAlreadyResolved,
  onForbidden,
}: CloseOutPaymentDialogProps) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const ids = useId();
  const titleId = `${ids}-title`;
  const choiceId = `${ids}-resolution`;

  const [resolution, setResolution] = useState<PaymentCloseOutResolution | null>(null);
  const [paymentId, setPaymentId] = useState('');
  const [reason, setReason] = useState('');
  // The server's word on the payment id staff named (409 INVALID), shown on that field.
  const [paymentIdError, setPaymentIdError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // One call per click, even when a second click lands before React re-renders.
  const inFlightRef = useRef(false);

  const uuid = reservation.reservationUuid;
  const settle = resolution === 'settle_to_payment';
  const trimmedReason = reason.trim();
  const trimmedPaymentId = paymentId.trim();
  const reasonValid = trimmedReason.length >= 1 && trimmedReason.length <= CLOSE_OUT_REASON_MAX;
  const ready =
    uuid !== null && resolution !== null && reasonValid && (!settle || trimmedPaymentId.length > 0);

  function close() {
    // A close-out in flight is un-dismissable: its answer is what the host reflects.
    if (submitting) return;
    onClose();
  }

  async function submit() {
    if (!uuid || resolution === null || !ready || inFlightRef.current) return;
    inFlightRef.current = true;
    setSubmitting(true);
    setError(null);
    setPaymentIdError(null);
    try {
      const result = await closeOutReservationPayment(uuid, {
        resolution,
        ...(settle ? { paymentId: trimmedPaymentId } : {}),
        reason: trimmedReason,
      });
      onClosedOut(result, resolution);
    } catch (err) {
      if (isPaymentNotStrandedRefusal(err)) {
        onAlreadyResolved();
      } else if (isCloseOutForbidden(err)) {
        onForbidden();
      } else if (isCloseOutPaymentInvalid(err)) {
        setPaymentIdError(toCloseOutPaymentErrorMessage(err));
      } else {
        setError(toCloseOutPaymentErrorMessage(err));
      }
    } finally {
      inFlightRef.current = false;
      setSubmitting(false);
    }
  }

  const confirmLabel =
    resolution === 'settle_to_payment'
      ? 'Mark paid'
      : resolution === 'no_charge'
        ? 'Mark unpaid'
        : 'Close out';

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth aria-labelledby={titleId}>
      <DialogTitle
        id={titleId}
        sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pb: 0.5 }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 500 }}>Close out payment</Typography>
          <Typography variant="body2">
            Record what happened to this payment. Nothing is charged or refunded.
          </Typography>
        </Box>
        <IconAction size="small" onClick={close} label="Close">
          <Icon name="close" sx={{ fontSize: 20 }} />
        </IconAction>
      </DialogTitle>

      <DialogContent>
        <Stack sx={{ gap: 1.5, mt: 0.5 }}>
          <RadioGroup
            aria-labelledby={choiceId}
            value={resolution ?? ''}
            onChange={(_event, next) => {
              setPaymentIdError(null);
              setResolution(next === 'settle_to_payment' ? 'settle_to_payment' : 'no_charge');
            }}
          >
            <Typography id={choiceId} sx={{ fontSize: 13, fontWeight: 600, color: t.text2 }}>
              What happened?
            </Typography>
            <FormControlLabel
              value="settle_to_payment"
              control={<Radio size="small" />}
              disabled={submitting}
              label={
                <Typography sx={{ fontSize: 14 }}>This payment belongs to the booking</Typography>
              }
            />
            <FormControlLabel
              value="no_charge"
              control={<Radio size="small" />}
              disabled={submitting}
              label={
                <Typography sx={{ fontSize: 14 }}>
                  Nothing was charged, or it was already refunded
                </Typography>
              }
            />
          </RadioGroup>

          {settle && (
            <TextField
              label="Payment id"
              size="small"
              required
              value={paymentId}
              onChange={(e) => {
                setPaymentId(e.target.value);
                setPaymentIdError(null);
              }}
              disabled={submitting}
              error={paymentIdError !== null}
              helperText={paymentIdError ?? CLOSE_OUT_PAYMENT_ID_HELP}
            />
          )}

          <TextField
            label="Reason"
            size="small"
            required
            multiline
            minRows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={submitting}
            error={trimmedReason.length > CLOSE_OUT_REASON_MAX}
            helperText={`${trimmedReason.length}/${CLOSE_OUT_REASON_MAX}`}
          />

          {error && (
            <Alert severity="error" role="alert">
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ borderTop: `1px solid ${t.lineSoft}`, px: 2.5, py: 1.5 }}>
        <Button color="inherit" onClick={close} disabled={submitting} sx={{ color: t.text2 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={
            submitting ? <CircularProgress size={16} color="inherit" /> : <Icon name="check" />
          }
          onClick={() => void submit()}
          disabled={submitting || !ready}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
